import { useEffect } from "react";

export type SeoData = {
  title: string;
  description: string;
  canonicalPath: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

const baseDescription = "Descubra, compare e acompanhe criadores brasileiros com sinais claros de presença, relevância e consistência.";

export function getSeoForPath(pathname: string, origin = "https://scorecreator.com.br"): SeoData {
  const cleanOrigin = origin.replace(/\/$/, "");
  const route = pathname.split("?")[0];
  let data: SeoData;
  if (route === "/") data = { title: "Score Creator — talento que dá para encontrar", description: baseDescription, canonicalPath: "/" };
  else if (route.startsWith("/criador/")) {
    const name = decodeURIComponent(route.split("/").pop() || "Criador").replace(/-/g, " ");
    data = { title: `${name.replace(/\b\w/g, (char) => char.toUpperCase())} | Score Creator`, description: `Perfil, links e Creator Score de ${name} no Score Creator.`, canonicalPath: route };
  } else if (route.startsWith("/categoria/") || route.startsWith("/categorias/")) {
    const category = decodeURIComponent(route.split("/").pop() || "criadores").replace(/-/g, " ");
    data = { title: `Criadores de ${category} | Score Creator`, description: `Encontre criadores de ${category} em destaque no Brasil.`, canonicalPath: route };
  } else if (route.startsWith("/ranking")) data = { title: "Ranking de criadores | Score Creator", description: "Acompanhe quem está construindo relevância com consistência.", canonicalPath: route };
  else if (route === "/categorias") data = { title: "Categorias | Score Creator", description: "Explore criadores por território criativo.", canonicalPath: route };
  else if (route === "/buscar") data = { title: "Buscar criadores | Score Creator", description: "Busque criadores por nome, categoria e cidade.", canonicalPath: route, noindex: true };
  else if (route === "/precos") data = { title: "Planos e preços | Score Creator", description: "Conheça o plano gratuito e o Premium do Score Creator.", canonicalPath: route };
  else if (route === "/sobre") data = { title: "Sobre | Score Creator", description: "Por que criamos uma vitrine mais clara para talentos brasileiros.", canonicalPath: route };
  else if (route === "/privacidade") data = { title: "Privacidade | Score Creator", description: "Política de privacidade e proteção de dados do Score Creator.", canonicalPath: route };
  else if (route === "/termos") data = { title: "Termos de uso | Score Creator", description: "Termos de uso da plataforma Score Creator.", canonicalPath: route };
  else if (route === "/diretrizes") data = { title: "Diretrizes da comunidade | Score Creator", description: "Regras para uma comunidade criativa segura e confiável.", canonicalPath: route };
  else if (route === "/contato") data = { title: "Contato | Score Creator", description: "Fale com a equipe do Score Creator.", canonicalPath: route };
  else if (route === "/como-funciona") data = { title: "Como funciona | Score Creator", description: "Conheça o perfil público, o Creator Score, a assinatura Premium e a descoberta.", canonicalPath: route };
  else if (route === "/faq") data = { title: "Perguntas frequentes | Score Creator", description: "Respostas sobre perfis, assinatura, indicação e Creator Score.", canonicalPath: route };
  else if (route.startsWith("/criadores/")) data = { title: "Criadores por categoria e cidade | Score Creator", description: "Descubra criadores brasileiros por categoria e localização.", canonicalPath: route, noindex: true };
  else if (route === "/404") data = { title: "Página não encontrada | Score Creator", description: "O endereço solicitado não foi encontrado.", canonicalPath: route, noindex: true };
  else data = { title: "Score Creator", description: baseDescription, canonicalPath: route, noindex: ["/dashboard", "/admin", "/login", "/entrar", "/cadastro", "/verificar-email", "/recuperar-senha", "/onboarding"].some((path) => route === path || route.startsWith(`${path}/`)) };

  if (!data.jsonLd && route === "/") {
    data.jsonLd = [
      { "@context": "https://schema.org", "@type": "Organization", name: "Score Creator", url: cleanOrigin, logo: `${cleanOrigin}/brand/logo-mark-clean-512.png` },
      { "@context": "https://schema.org", "@type": "WebSite", name: "Score Creator", url: cleanOrigin, potentialAction: { "@type": "SearchAction", target: `${cleanOrigin}/buscar?q={search_term_string}`, "query-input": "required name=search_term_string" } },
    ];
  }
  return data;
}

export function buildHead(data: SeoData, origin: string) {
  const canonical = `${origin.replace(/\/$/, "")}${data.canonicalPath}`;
  const socialImage = new URL(data.image || "/brand/social-card.png", origin).toString();
  const safeJsonLd = data.jsonLd ? JSON.stringify(data.jsonLd).replace(/</g, "\\u003c") : null;
  return [
    `<title>${escapeHtml(data.title)}</title>`,
    `<meta name="description" content="${escapeHtml(data.description)}">`,
    `<link rel="canonical" href="${escapeHtml(canonical)}">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:locale" content="pt_BR">`,
    `<meta property="og:site_name" content="Score Creator">`,
    `<meta property="og:title" content="${escapeHtml(data.title)}">`,
    `<meta property="og:description" content="${escapeHtml(data.description)}">`,
    `<meta property="og:url" content="${escapeHtml(canonical)}">`,
    `<meta property="og:image" content="${escapeHtml(socialImage)}">`,
    `<meta property="og:image:width" content="1200">`,
    `<meta property="og:image:height" content="630">`,
    `<meta property="og:image:alt" content="Score Creator - talento que da para encontrar">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${escapeHtml(data.title)}">`,
    `<meta name="twitter:description" content="${escapeHtml(data.description)}">`,
    `<meta name="twitter:image" content="${escapeHtml(socialImage)}">`,
    data.noindex ? `<meta name="robots" content="noindex, nofollow">` : `<meta name="robots" content="index, follow">`,
    safeJsonLd ? `<script type="application/ld+json">${safeJsonLd}</script>` : "",
  ].join("\n");
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[character] || character);
}

export function Seo({ data }: { data: SeoData }) {
  useEffect(() => {
    document.title = data.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", data.description);
    const robots = document.querySelector('meta[name="robots"]');
    robots?.setAttribute("content", data.noindex ? "noindex, nofollow" : "index, follow");
    const canonical = document.querySelector('link[rel="canonical"]');
    const origin = window.location.origin;
    canonical?.setAttribute("href", `${origin}${data.canonicalPath}`);
  }, [data]);
  return null;
}
